import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEndpoints } from '../store/slices/endpointsSlice';
import { fetchRequests } from '../store/slices/requestsSlice';
import { useEnvironment } from '../contexts/EnvironmentContext';
import ResponseTimeHistogram from '../components/ResponseTimeHistogram';
import api from '../utils/api';

const Dashboard = () => {
  const dispatch = useDispatch();
  const { endpoints, loading } = useSelector((state) => state.endpoints);
  const { requests } = useSelector((state) => state.requests);
  const { selectedEnvironmentId, selectedEnvironment, loading: envLoading } = useEnvironment();
  const [slowestEndpoints, setSlowestEndpoints] = useState([]);
  const [loadingSlowest, setLoadingSlowest] = useState(true);
  const [averageResponseTime, setAverageResponseTime] = useState(null);
  const [loadingAvgResponseTime, setLoadingAvgResponseTime] = useState(true);
  const [responseTimeDistribution, setResponseTimeDistribution] = useState(null);
  const [loadingDistribution, setLoadingDistribution] = useState(true);
  // Slowest endpoints and the distribution cover the last N days (from the API).
  const [statsWindowDays, setStatsWindowDays] = useState(null);

  useEffect(() => {
    dispatch(fetchEndpoints());
    dispatch(fetchRequests());
  }, [dispatch]);

  useEffect(() => {
    if (selectedEnvironmentId) {
      fetchSlowestEndpoints();
      fetchAverageResponseTime();
      fetchResponseTimeDistribution();
    }
  }, [selectedEnvironmentId]);

  const fetchSlowestEndpoints = async () => {
    try {
      setLoadingSlowest(true);
      const params = selectedEnvironmentId ? { environment_id: selectedEnvironmentId } : {};
      const response = await api.get('/endpoints/slowest', { params });
      setSlowestEndpoints(response.data.endpoints || []);
      if (response.data.window_days) setStatsWindowDays(response.data.window_days);
    } catch (error) {
      console.error('Failed to fetch slowest endpoints:', error);
      setSlowestEndpoints([]);
    } finally {
      setLoadingSlowest(false);
    }
  };

  const fetchAverageResponseTime = async () => {
    try {
      setLoadingAvgResponseTime(true);
      const params = selectedEnvironmentId ? { environment_id: selectedEnvironmentId } : {};
      const response = await api.get('/endpoints/average-response-time', { params });
      setAverageResponseTime(response.data);
    } catch (error) {
      console.error('Failed to fetch average response time:', error);
      setAverageResponseTime(null);
    } finally {
      setLoadingAvgResponseTime(false);
    }
  };

  const fetchResponseTimeDistribution = async () => {
    try {
      setLoadingDistribution(true);
      const params = selectedEnvironmentId ? { environment_id: selectedEnvironmentId } : {};
      const response = await api.get('/endpoints/response-time-distribution', { params });
      setResponseTimeDistribution(response.data);
      if (response.data.window_days) setStatsWindowDays(response.data.window_days);
    } catch (error) {
      console.error('Failed to fetch response time distribution:', error);
      setResponseTimeDistribution(null);
    } finally {
      setLoadingDistribution(false);
    }
  };

  // Filter data by selected environment
  const filteredRequests = selectedEnvironmentId && selectedEnvironmentId !== null
    ? requests.filter(request => request.environment_id === selectedEnvironmentId)
    : requests;

  // Get endpoint IDs that are used in filtered requests
  const endpointIdsInUse = new Set();
  filteredRequests.forEach(request => {
    if (request.endpoint_id) endpointIdsInUse.add(request.endpoint_id);
    if (request.endpoint_a_id) endpointIdsInUse.add(request.endpoint_a_id);
    if (request.endpoint_b_id) endpointIdsInUse.add(request.endpoint_b_id);
  });
  
  // Filter endpoints to only those used in the selected environment
  const filteredEndpoints = selectedEnvironmentId && selectedEnvironmentId !== null
    ? endpoints.filter(endpoint => endpointIdsInUse.has(endpoint.id))
    : endpoints;

  // Find requests with data mismatches (both endpoints succeeded but data differs)
  const getMismatchedRequests = () => {
    return filteredRequests.filter(request => {
      if (request.test_type !== 'data_comparison') return false;
      if (!request.results || request.results.length === 0) return false;
      
      const lastResult = request.results[0];
      // Mismatch means both endpoints returned 2xx but success is false
      return lastResult.endpoint_a_status >= 200 && 
             lastResult.endpoint_a_status < 300 &&
             lastResult.endpoint_b_status >= 200 && 
             lastResult.endpoint_b_status < 300 &&
             !lastResult.success;
    });
  };

  // Get endpoint IDs that have data quality issues (mismatches)
  const getEndpointIdsWithMismatches = () => {
    const mismatchedRequests = getMismatchedRequests();
    const endpointIds = new Set();
    
    mismatchedRequests.forEach(request => {
      if (request.endpoint_a_id) endpointIds.add(request.endpoint_a_id);
      if (request.endpoint_b_id) endpointIds.add(request.endpoint_b_id);
    });
    
    return endpointIds;
  };

  const endpointIdsWithMismatches = getEndpointIdsWithMismatches();
  const dataQualityIssuesCount = getMismatchedRequests().length;

  // Helper function to determine if an endpoint is healthy based on status code
  const isHealthyEndpoint = (endpoint) => {
    // If endpoint has a data quality issue (mismatch), it's not healthy
    if (endpointIdsWithMismatches.has(endpoint.id)) {
      return false;
    }
    
    if (endpoint.last_status_code) {
      return endpoint.last_status_code >= 200 && endpoint.last_status_code < 300;
    }
    // Fall back to is_active if no status code yet
    return endpoint.is_active;
  };

  // Helper function to determine if an endpoint has an error
  const isErrorEndpoint = (endpoint) => {
    if (endpoint.last_status_code) {
      return endpoint.last_status_code >= 400;
    }
    // Fall back to is_active if no status code yet
    return !endpoint.is_active;
  };

  const healthyCount = filteredEndpoints.filter(isHealthyEndpoint).length;
  const errorCount = filteredEndpoints.filter(isErrorEndpoint).length;

  return (
    <div className="page-content">
      <div className="dashboard-grid">
        <div className="stats-card">
          <div className="stats-label">Total Endpoints</div>
          <div className="stats-value">{filteredEndpoints.length}</div>
        </div>
        <div className="stats-card">
          <div className="stats-label">Healthy Endpoints</div>
          <div className="stats-value">
            {healthyCount}
          </div>
        </div>
        <div className="stats-card">
          <div className="stats-label">Error Endpoints</div>
          <div className="stats-value">
            {errorCount}
          </div>
        </div>
        <div className="stats-card">
          <div className="stats-label">Data Quality Issues</div>
          <div className="stats-value">{dataQualityIssuesCount}</div>
        </div>
        <div className="stats-card">
          <div className="stats-label">Average Response Time</div>
          <div className="stats-value">
            {loadingAvgResponseTime ? (
              <span className="response-time">...</span>
            ) : averageResponseTime?.averageResponseTime !== null ? (
              <>
                <span className="response-time">{averageResponseTime.averageResponseTime}</span>
                <span className="response-time-unit">ms</span>
              </>
            ) : (
              <span className="response-time">-</span>
            )}
          </div>
          {averageResponseTime && averageResponseTime.includedCount > 0 && (
            <div className="stats-subtitle">
              from {averageResponseTime.includedCount} endpoint{averageResponseTime.includedCount !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>

      {/* Second Row - Analytics Modules */}
      <div className="dashboard-row">
        {/* Slowest Endpoints */}
        <div className="dashboard-module">
          <div className="module-header">
            <h3>Slowest Endpoints</h3>
            <span className="module-subtitle">
              Top 5 by avg response time{statsWindowDays ? ` · last ${statsWindowDays} days` : ''}
            </span>
          </div>
          <div className="module-body">
            {loadingSlowest ? (
              <div className="loading-state">Loading...</div>
            ) : slowestEndpoints.length === 0 ? (
              <div className="empty-state">
                <p>No response time data available yet</p>
                <span className="empty-state-hint">Run some tests to see performance metrics</span>
              </div>
            ) : (
              <div className="slowest-endpoints-list">
                {slowestEndpoints.map((endpoint, index) => (
                  <div key={endpoint.id} className="slowest-endpoint-item">
                    <div className="endpoint-rank">#{index + 1}</div>
                    <div className="endpoint-info">
                      <span className="endpoint-name" title={endpoint.url}>
                        {endpoint.name}
                      </span>
                      <span className={`endpoint-method-badge badge-method badge-${endpoint.method.toLowerCase()}`}>
                        {endpoint.method}
                      </span>
                    </div>
                    <div className="endpoint-metrics">
                      <span className="response-time-badge">
                        {endpoint.avg_response_time} ms
                      </span>
                      <span className="test-count-hint">
                        {endpoint.test_count} test{endpoint.test_count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Response Time Distribution */}
        <div className="dashboard-module">
          <div className="module-header">
            <h3>Response Time Distribution</h3>
            <span className="module-subtitle">
              Log scale histogram{statsWindowDays ? ` · last ${statsWindowDays} days` : ''}
            </span>
          </div>
          <div className="module-body">
            {loadingDistribution ? (
              <div className="loading-state">Loading...</div>
            ) : (
              <ResponseTimeHistogram data={responseTimeDistribution} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

